/**
 * TrackingPage - i18n wiring (Phase 5)
 *
 * Translation namespaces used:
 *   - tracking.*        (extended in Phase 5: rideNumber, connected, disconnected,
 *                        estimatedArrival, distance, enRouteToYou, pickup,
 *                        pickupMarker, yourDriverMarker, backToHome, unableToLoad,
 *                        statusAssigned/Accepted/Arriving/InProgress/Completed/Cancelled)
 *
 * Hardcoded values left in place (intentional):
 *   - dark Google Maps style hex colours
 *   - SVG marker template (no human-readable text)
 *   - bookingId.slice() formatting
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Car, MapPin, Clock, Phone, User, Navigation,
  CheckCircle2, Loader2, XCircle, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { loadGoogleMaps } from '../lib/googleMapsLoader';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const TrackingPage = () => {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const wsRef = useRef(null);

  const [booking, setBooking] = useState(null);
  const [trackingSession, setTrackingSession] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Fetch booking details
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        // Fetch tracking session
        const response = await axios.get(`${API_URL}/tracking/session/${bookingId}`);
        setTrackingSession(response.data);
        setDriverLocation(response.data.driver_location);

        // Also fetch booking details
        const bookingRes = await axios.get(`${API_URL}/bookings/${bookingId}`);
        setBooking(bookingRes.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching tracking data:', err);
        setError(t('tracking.unableToLoad'));
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, t]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!bookingId) return;

    const wsUrl = `${import.meta.env.VITE_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/tracking/client/${bookingId}`;

    const connectWebSocket = () => {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'location_update') {
          setDriverLocation({
            lat: data.location.latitude,
            lng: data.location.longitude,
            heading: data.location.heading,
            speed: data.location.speed
          });

          // Update marker on map
          updateDriverMarker(data.location.latitude, data.location.longitude);
        } else if (data.type === 'initial_state') {
          setTrackingSession(data.session);
          if (data.session?.driver_location) {
            setDriverLocation(data.session.driver_location);
          }
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        // Reconnect after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [bookingId]);

  // Initialize Google Map
  useEffect(() => {
    if (!trackingSession || !mapRef.current) return;

    const initMap = () => {
      const pickup = trackingSession.pickup;
      const center = driverLocation || pickup;

      if (!center?.lat || !center?.lng) return;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: center.lat, lng: center.lng },
        zoom: 14,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#212121" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1a1a1a" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
        ],
        disableDefaultUI: true,
        zoomControl: true,
      });

      // Add pickup marker
      if (pickup?.lat && pickup?.lng) {
        pickupMarkerRef.current = new window.google.maps.Marker({
          position: { lat: pickup.lat, lng: pickup.lng },
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#22c55e",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
          title: t('tracking.pickupMarker')
        });
      }

      // Add driver marker
      if (driverLocation?.lat && driverLocation?.lng) {
        driverMarkerRef.current = new window.google.maps.Marker({
          position: { lat: driverLocation.lat, lng: driverLocation.lng },
          map: mapInstanceRef.current,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="18" fill="#000" stroke="#fff" stroke-width="3"/>
                <path d="M12 22 L20 12 L28 22 L20 18 Z" fill="#fff"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
          },
          title: t('tracking.yourDriverMarker')
        });
      }

      // Initialize directions renderer
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: "#3b82f6",
          strokeWeight: 4,
          strokeOpacity: 0.8
        }
      });

      // Calculate route
      if (driverLocation?.lat && pickup?.lat) {
        calculateRoute();
      }
    };

    loadGoogleMaps({ apiKey: GOOGLE_MAPS_API_KEY, language: 'fr' })
      .then(initMap)
      .catch((err) => console.error('Google Maps load failed:', err));
  }, [trackingSession]);

  const updateDriverMarker = useCallback((lat, lng) => {
    if (driverMarkerRef.current && mapInstanceRef.current) {
      driverMarkerRef.current.setPosition({ lat, lng });
      mapInstanceRef.current.panTo({ lat, lng });
      calculateRoute();
    }
  }, []);

  const calculateRoute = useCallback(async () => {
    if (!driverLocation?.lat || !trackingSession?.pickup?.lat) return;

    try {
      const response = await axios.get(`${API_URL}/tracking/eta/${bookingId}`);
      setEta(response.data);

      // Draw route on map
      if (directionsRendererRef.current && window.google) {
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route({
          origin: { lat: driverLocation.lat, lng: driverLocation.lng },
          destination: { lat: trackingSession.pickup.lat, lng: trackingSession.pickup.lng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (result, status) => {
          if (status === 'OK') {
            directionsRendererRef.current.setDirections(result);
          }
        });
      }
    } catch (err) {
      console.error('Error calculating route:', err);
    }
  }, [driverLocation, trackingSession, bookingId]);

  const getStatusInfo = (status) => {
    const statuses = {
      assigned: { icon: Car, color: 'text-blue-400', bg: 'bg-blue-500/20', label: t('tracking.statusAssigned') },
      accepted: { icon: CheckCircle2, color: 'text-blue-400', bg: 'bg-blue-500/20', label: t('tracking.statusAccepted') },
      driver_arriving: { icon: Navigation, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: t('tracking.statusArriving') },
      in_progress: { icon: Car, color: 'text-green-400', bg: 'bg-green-500/20', label: t('tracking.statusInProgress') },
      completed: { icon: CheckCircle2, color: 'text-neutral-400', bg: 'bg-neutral-500/20', label: t('tracking.statusCompleted') },
      cancelled: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: t('tracking.statusCancelled') },
    };
    return statuses[status] || statuses.assigned;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2B2B2B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#2B2B2B] flex flex-col items-center justify-center p-4">
        <XCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-white text-center mb-4">{error}</p>
        <Button onClick={() => navigate('/')}>{t('tracking.backToHome')}</Button>
      </div>
    );
  }

  const statusInfo = booking ? getStatusInfo(booking.status) : getStatusInfo('assigned');
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-[#2B2B2B] flex flex-col">
      <Seo {...seo.tracking} />
      {/* Header */}
      <header className="bg-black border-b border-white/10 p-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-white font-heading font-bold">{t('tracking.title')}</h1>
            <p className="text-white/50 text-sm">{t('tracking.rideNumber')} #{bookingId?.slice(0, 8).toUpperCase()}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-xs text-white/50">{wsConnected ? t('tracking.connected') : t('tracking.disconnected')}</span>
          </div>
        </div>
      </header>

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapRef} className="w-full h-full min-h-[300px]" />

        {/* ETA Overlay */}
        {eta && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 left-4 right-4 bg-black/90 backdrop-blur-sm border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-xs">{t('tracking.estimatedArrival')}</p>
                <p className="text-white text-2xl font-bold">{eta.duration_text || `${Math.round(eta.duration_seconds / 60)} min`}</p>
              </div>
              <div className="text-right">
                <p className="text-white/50 text-xs">{t('tracking.distance')}</p>
                <p className="text-white font-medium">{eta.distance_text || `${(eta.distance_meters / 1000).toFixed(1)} km`}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom Panel */}
      <div className="bg-black border-t border-white/10 p-4 space-y-4">
        {/* Status Badge */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusInfo.bg} w-fit`}>
          <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
          <span className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
        </div>

        {/* Driver Info */}
        {trackingSession && (
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">{booking?.driver_name || t('tracking.yourDriver')}</p>
              <p className="text-white/50 text-sm">{booking?.vehicle_info || t('tracking.enRouteToYou')}</p>
            </div>
            {booking?.driver_phone && (
              <a
                href={`tel:${booking.driver_phone}`}
                className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center"
              >
                <Phone className="w-5 h-5 text-white" />
              </a>
            )}
          </div>
        )}

        {/* Ride Details */}
        {booking && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div>
                <p className="text-white/50 text-xs">{t('tracking.pickup')}</p>
                <p className="text-white text-sm">{booking.pickup_address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
              </div>
              <div>
                <p className="text-white/50 text-xs">{t('booking.destination')}</p>
                <p className="text-white text-sm">{booking.destination_address}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingPage;
